import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Wrench, Repeat, CalendarClock, FolderPlus } from "lucide-react";
import { toast } from "sonner";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { AssistanceTemplatesCard } from "@/components/assistance-templates-card";
import {
  useCreateServiceGroup,
  useCreateServiceType,
  useDeleteServiceGroup,
  useServiceGroups,
  useServiceTypes,
  useUpdateServiceGroup,
  useUpdateServiceType,
} from "@/hooks/queries";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import type { ServiceGroup, ServiceType } from "@/services/gpsdrive-assistance.service";

export const Route = createFileRoute("/admin/assistance")({
  component: () => (
    <AdminAccessGate action="assistencia">
      <AssistancePage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Assistência — Administração — Wp Rastreamento" }] }),
});

const NO_GROUP = "__no_group__";

function AssistancePage() {
  const { data: types, isLoading: loadingTypes } = useServiceTypes();
  const { data: groups, isLoading: loadingGroups } = useServiceGroups();

  const sections = useMemo(() => {
    if (!types) return [];
    if (!groups || groups.length === 0) {
      return [{ group: null as ServiceGroup | null, types }];
    }
    const byGroup = new Map<string, ServiceType[]>();
    const ungrouped: ServiceType[] = [];
    for (const type of types) {
      if (!type.groupId) {
        ungrouped.push(type);
        continue;
      }
      const list = byGroup.get(type.groupId) ?? [];
      list.push(type);
      byGroup.set(type.groupId, list);
    }
    const grouped = groups.map((group) => ({ group, types: byGroup.get(group.id) ?? [] }));
    return ungrouped.length > 0 ? [...grouped, { group: null, types: ungrouped }] : grouped;
  }, [types, groups]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Configurar Assistência</h1>
        <p className="text-sm text-muted-foreground">
          Cada tipo de serviço tem sua própria regra — quantas vezes pode ser usado por ano e o
          intervalo mínimo entre solicitações. Agrupe os serviços (ex.: "Assistência 100km") para
          organizar a tela e vincular um nível de assistência a um Plano.
        </p>
      </div>

      <ServiceGroupsSection groups={groups ?? []} isLoading={loadingGroups} />

      <AssistanceTemplatesCard />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Tipos de serviço</h2>
          <p className="text-sm text-muted-foreground">
            Desativar (nunca excluir) preserva o histórico de assistências já registradas com este tipo.
          </p>
        </div>
        <ServiceTypeFormDialog
          groups={groups ?? []}
          trigger={
            <Button>
              <Plus className="h-4 w-4" />
              Cadastrar tipo de serviço
            </Button>
          }
        />
      </div>

      {loadingTypes ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : !types || types.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <Wrench className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhum tipo de serviço cadastrado ainda</p>
            <p className="text-xs text-muted-foreground">
              Cadastre pelo menos um tipo (ex.: "Auxílio Guincho") para liberar o "Registrar
              Assistência" no app.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {sections.map(({ group, types: sectionTypes }) => (
            <div key={group?.id ?? NO_GROUP} className="space-y-3">
              {sections.length > 1 && (
                <h3 className="text-sm font-semibold text-muted-foreground">
                  {group ? group.name : "Sem grupo"}
                </h3>
              )}
              {sectionTypes.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum serviço neste grupo ainda.</p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {sectionTypes.map((type) => (
                    <ServiceTypeCard key={type.id} type={type} groups={groups ?? []} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ServiceGroupsSection({ groups, isLoading }: { groups: ServiceGroup[]; isLoading: boolean }) {
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Grupos de assistência</CardTitle>
          <CardDescription>
            Ex.: "Assistência 100km". Um Plano "com assistência" vincula um destes grupos.
          </CardDescription>
        </div>
        <ServiceGroupFormDialog
          trigger={
            <Button variant="outline" size="sm">
              <FolderPlus className="h-3.5 w-3.5" />
              Novo grupo
            </Button>
          }
        />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhum grupo criado ainda — os serviços cadastrados aparecem soltos, sem seção.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {groups.map((group) => (
              <ServiceGroupChip key={group.id} group={group} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ServiceGroupChip({ group }: { group: ServiceGroup }) {
  const deleteGroup = useDeleteServiceGroup();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteGroup.mutateAsync(group.id);
      toast.success("Grupo excluído");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao excluir o grupo");
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <div className="flex items-center gap-1 rounded-full border bg-muted/40 py-1 pl-3 pr-1">
      <span className="text-sm font-medium">{group.name}</span>
      <ServiceGroupFormDialog
        group={group}
        trigger={
          <Button variant="ghost" size="icon" className="h-6 w-6">
            <Pencil className="h-3 w-3" />
          </Button>
        }
      />
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setConfirmOpen(true)}>
          <Trash2 className="h-3 w-3" />
        </Button>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir grupo "{group.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Os serviços deste grupo não são apagados — ficam "sem grupo". Planos vinculados a
              este grupo ficam "sem assistência".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteGroup.isPending}>
              {deleteGroup.isPending ? "Excluindo…" : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ServiceGroupFormDialog({ group, trigger }: { group?: ServiceGroup; trigger: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const createGroup = useCreateServiceGroup();
  const updateGroup = useUpdateServiceGroup();
  const isEditing = Boolean(group);
  const isPending = createGroup.isPending || updateGroup.isPending;

  useEffect(() => {
    if (!open) return;
    setName(group?.name ?? "");
  }, [open, group]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Informe o nome do grupo");
      return;
    }
    try {
      if (group) {
        await updateGroup.mutateAsync({ id: group.id, input: { name: name.trim() } });
        toast.success("Grupo atualizado");
      } else {
        await createGroup.mutateAsync({ name: name.trim() });
        toast.success("Grupo criado");
      }
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar o grupo");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar grupo" : "Novo grupo de assistência"}</DialogTitle>
          <DialogDescription>Ex.: "Assistência 100km", "Assistência 200km".</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="service-group-name">Nome do grupo</Label>
          <Input
            id="service-group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Assistência 100km"
          />
        </div>
        <DialogFooter>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ServiceTypeCard({ type, groups }: { type: ServiceType; groups: ServiceGroup[] }) {
  const updateType = useUpdateServiceType();

  const handleToggle = async (active: boolean) => {
    try {
      await updateType.mutateAsync({ id: type.id, data: { active } });
      toast.success(active ? "Tipo de serviço ativado" : "Tipo de serviço desativado");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao atualizar o tipo de serviço");
    }
  };

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="flex-1">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">{type.name}</CardTitle>
          <Switch checked={type.active} onCheckedChange={handleToggle} disabled={updateType.isPending} />
        </div>
        <CardDescription>{type.active ? "Ativo" : "Desativado"}</CardDescription>
        <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Repeat className="h-3.5 w-3.5 shrink-0" />
            Até {type.annualLimit} {type.annualLimit === 1 ? "utilização" : "utilizações"} por ano
          </div>
          <div className="flex items-center gap-1.5">
            <CalendarClock className="h-3.5 w-3.5 shrink-0" />
            {type.minIntervalDays === 0
              ? "Sem intervalo mínimo entre solicitações"
              : `Mínimo ${type.minIntervalDays} ${type.minIntervalDays === 1 ? "dia" : "dias"} entre solicitações`}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex items-center justify-end gap-2 pt-0">
        <ServiceTypeFormDialog
          type={type}
          groups={groups}
          trigger={
            <Button variant="outline" size="sm">
              <Pencil className="h-3.5 w-3.5" />
              Editar
            </Button>
          }
        />
      </CardContent>
    </Card>
  );
}

function ServiceTypeFormDialog({
  type,
  groups,
  trigger,
}: {
  type?: ServiceType;
  groups: ServiceGroup[];
  trigger: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [annualLimit, setAnnualLimit] = useState("3");
  const [minIntervalDays, setMinIntervalDays] = useState("30");
  const [groupId, setGroupId] = useState<string>(NO_GROUP);
  const createType = useCreateServiceType();
  const updateType = useUpdateServiceType();
  const isEditing = Boolean(type);
  const isPending = createType.isPending || updateType.isPending;

  useEffect(() => {
    if (!open) return;
    setName(type?.name ?? "");
    setAnnualLimit(type ? String(type.annualLimit) : "3");
    setMinIntervalDays(type ? String(type.minIntervalDays) : "30");
    setGroupId(type?.groupId ?? NO_GROUP);
  }, [open, type]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Informe o nome do serviço");
      return;
    }
    const parsedAnnualLimit = Number(annualLimit);
    if (!Number.isInteger(parsedAnnualLimit) || parsedAnnualLimit < 1) {
      toast.error("Informe um limite anual válido (mínimo 1)");
      return;
    }
    const parsedMinIntervalDays = Number(minIntervalDays);
    if (!Number.isInteger(parsedMinIntervalDays) || parsedMinIntervalDays < 0) {
      toast.error("Informe um intervalo mínimo válido (0 ou mais dias)");
      return;
    }
    const resolvedGroupId = groupId === NO_GROUP ? null : groupId;
    try {
      if (type) {
        await updateType.mutateAsync({
          id: type.id,
          data: {
            name: name.trim(),
            annualLimit: parsedAnnualLimit,
            minIntervalDays: parsedMinIntervalDays,
            groupId: resolvedGroupId,
          },
        });
        toast.success("Tipo de serviço atualizado");
      } else {
        await createType.mutateAsync({
          name: name.trim(),
          annualLimit: parsedAnnualLimit,
          minIntervalDays: parsedMinIntervalDays,
          groupId: resolvedGroupId,
        });
        toast.success("Tipo de serviço cadastrado");
      }
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar o tipo de serviço");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar tipo de serviço" : "Cadastrar tipo de serviço"}</DialogTitle>
          <DialogDescription>
            Fica disponível para escolher em "Registrar Assistência" no app do cliente. As regras
            abaixo são só deste serviço — outros tipos continuam com as próprias regras,
            independentes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="service-type-name">Nome do serviço</Label>
            <Input
              id="service-type-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Auxílio Guincho"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="service-type-group">Grupo</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger id="service-type-group">
                <SelectValue placeholder="Sem grupo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_GROUP}>Sem grupo</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="service-type-annual-limit">Limite anual (utilizações)</Label>
              <Input
                id="service-type-annual-limit"
                value={annualLimit}
                onChange={(e) => setAnnualLimit(e.target.value)}
                inputMode="numeric"
                placeholder="Ex: 3"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-type-min-interval">Intervalo mínimo (dias)</Label>
              <Input
                id="service-type-min-interval"
                value={minIntervalDays}
                onChange={(e) => setMinIntervalDays(e.target.value)}
                inputMode="numeric"
                placeholder="Ex: 30"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Exemplo: limite 3 e intervalo 30 dias — se o cliente solicitar em 10/09, só poderá
            solicitar de novo a partir de 11/10, e no máximo 3 vezes dentro do ano.
          </p>
        </div>

        <DialogFooter>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
